/**
 * Aurora Canvas 自动保存 Hook（React Flow 版）
 * 监听 nodes/edges/viewport 变化，debounce 5s 后 PATCH 到云端
 */

import { useEffect, useRef } from 'react'
import type { Edge, Node, Viewport } from '@xyflow/react'
import { canvasApi } from '../utils/canvas-api'
import { useCanvasStore } from '../stores/canvas'

const DEBOUNCE_MS = 5000

interface AutosaveParams {
  nodes: Node[]
  edges: Edge[]
  viewport: Viewport
  canvasId: string | null
  ready: boolean
}

export function useCanvasAutosave({ nodes, edges, viewport, canvasId, ready }: AutosaveParams) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dirtyRef = useRef(false)
  const inflightRef = useRef(false)
  const firstRunRef = useRef(true)

  const latestRef = useRef({ nodes, edges, viewport })
  latestRef.current = { nodes, edges, viewport }

  // 切换画布时重置 firstRunRef，避免把刚加载的快照当作 dirty 再保存一次
  useEffect(() => {
    firstRunRef.current = true
  }, [canvasId])

  useEffect(() => {
    if (!ready || !canvasId) return

    // 跳过水合后的首次 effect（loadSnapshot 注入造成的伪变化）
    if (firstRunRef.current) {
      firstRunRef.current = false
      return
    }

    dirtyRef.current = true

    if (timerRef.current) clearTimeout(timerRef.current)

    timerRef.current = setTimeout(async () => {
      if (inflightRef.current) return
      if (!dirtyRef.current) return
      inflightRef.current = true
      dirtyRef.current = false

      const store = useCanvasStore.getState()
      store.markSaving()

      try {
        const snapshot = {
          nodes: latestRef.current.nodes,
          edges: latestRef.current.edges,
          viewport: latestRef.current.viewport,
        }
        await canvasApi.update(canvasId, { data: snapshot })
        useCanvasStore.getState().markSaved()
      } catch (err) {
        console.error('[Canvas] autosave failed:', err)
        const msg = err instanceof Error ? err.message : '保存失败'
        useCanvasStore.getState().markError(msg)
        dirtyRef.current = true
      } finally {
        inflightRef.current = false
      }
    }, DEBOUNCE_MS)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [nodes, edges, viewport, canvasId, ready])

  // 卸载时若仍有 pending 变更，立即 flush
  useEffect(() => {
    return () => {
      if (!dirtyRef.current || !canvasId) return
      const snapshot = {
        nodes: latestRef.current.nodes,
        edges: latestRef.current.edges,
        viewport: latestRef.current.viewport,
      }
      void canvasApi.update(canvasId, { data: snapshot }).catch((err) => {
        console.error('[Canvas] final flush failed:', err)
      })
    }
  }, [canvasId])
}
